import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UiState {
  // Global loading states
  isGlobalLoading: boolean;
  globalLoadingMessage: string;
  
  // Notifications
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    autoHide?: boolean;
    duration?: number;
  }>;
  
  // Modals and dialogs
  modals: {
    deleteConfirm: {
      isOpen: boolean;
      itemId: string | null;
      itemTitle: string;
    };
    preview: {
      isOpen: boolean;
      itemId: string | null;
    };
  };
  
  // UI preferences
  preferences: {
    tablePageSize: number;
    defaultFilters: any;
    sidebarCollapsed: boolean;
  };
}

const initialState: UiState = {
  isGlobalLoading: false,
  globalLoadingMessage: '',
  notifications: [],
  modals: {
    deleteConfirm: {
      isOpen: false,
      itemId: null,
      itemTitle: '',
    },
    preview: {
      isOpen: false,
      itemId: null,
    },
  },
  preferences: {
    tablePageSize: 30,
    defaultFilters: {},
    sidebarCollapsed: false,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setGlobalLoading: (state, action: PayloadAction<{ isLoading: boolean; message?: string }>) => {
      state.isGlobalLoading = action.payload.isLoading;
      state.globalLoadingMessage = action.payload.message || '';
    },
    
    addNotification: (state, action: PayloadAction<{
      type: 'success' | 'error' | 'warning' | 'info';
      message: string;
      autoHide?: boolean;
      duration?: number;
    }>) => {
      const notification = {
        id: Date.now().toString(),
        autoHide: true,
        duration: 5000,
        ...action.payload,
      };
      state.notifications.push(notification);
    },
    
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    
    clearNotifications: (state) => {
      state.notifications = [];
    },
    
    openDeleteConfirm: (state, action: PayloadAction<{ itemId: string; itemTitle: string }>) => {
      state.modals.deleteConfirm = {
        isOpen: true,
        itemId: action.payload.itemId,
        itemTitle: action.payload.itemTitle,
      };
    },
    
    closeDeleteConfirm: (state) => {
      state.modals.deleteConfirm = {
        isOpen: false,
        itemId: null,
        itemTitle: '',
      };
    },
    
    openPreview: (state, action: PayloadAction<string>) => {
      state.modals.preview = {
        isOpen: true,
        itemId: action.payload,
      };
    },
    
    closePreview: (state) => {
      state.modals.preview = {
        isOpen: false,
        itemId: null,
      };
    },
    
    updatePreferences: (state, action: PayloadAction<Partial<UiState['preferences']>>) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },
  },
});

export const {
  setGlobalLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  openDeleteConfirm,
  closeDeleteConfirm,
  openPreview,
  closePreview,
  updatePreferences,
} = uiSlice.actions;

export default uiSlice.reducer;
